import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, Clock, Calendar, Plus, Target, Wrench, Warning, CheckCircle, PencilSimple, ChartLineUp, Notepad, Lightning, MagicWand } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import EquipmentGauge from "./EquipmentGauge";
import LogEquipmentMetricsModal from "./LogEquipmentMetricsModal";
import EditEquipmentTargetsModal from "./EditEquipmentTargetsModal";
import EquipmentNotesPanel from "./EquipmentNotesPanel";
import EquipmentFaultsPanel from "./EquipmentFaultsPanel";
import EquipmentFaultFinderPanel from "./EquipmentFaultFinderPanel";
import { useAuth } from "../context/AuthContext";

function todayStr() {
    return new Date().toISOString().slice(0, 10);
}
function daysAgoStr(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}
function firstOfMonthStr() {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

const PRESETS = [
    { key: "today", label: "Today", start: () => todayStr(), end: () => todayStr() },
    { key: "7d", label: "Last 7 days", start: () => daysAgoStr(6), end: () => todayStr() },
    { key: "14d", label: "Last 14 days", start: () => daysAgoStr(13), end: () => todayStr() },
    { key: "30d", label: "Last 30 days", start: () => daysAgoStr(29), end: () => todayStr() },
    { key: "month", label: "This month", start: () => firstOfMonthStr(), end: () => todayStr() },
];

export default function EquipmentDashboardModal({ equipmentId, onClose, canInput, initialTab = "performance" }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(daysAgoStr(6));
    const [endDate, setEndDate] = useState(todayStr());
    const [showLog, setShowLog] = useState(false);
    const [showTargets, setShowTargets] = useState(false);
    const [now, setNow] = useState(new Date());
    const [tab, setTab] = useState(initialTab);
    const role = user?.role;
    const canEditNotes = ["electrician", "admin", "manager"].includes(role);
    const canEditFaults = ["electrician", "admin", "manager"].includes(role);
    const canRunFaultFinder = ["electrician", "admin", "manager"].includes(role);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/equipment/${equipmentId}/dashboard`, {
                params: { start_date: startDate, end_date: endDate },
            });
            setData(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [equipmentId, startDate, endDate]);

    useEffect(() => {
        load();
        const t = setInterval(() => setNow(new Date()), 30_000);
        return () => clearInterval(t);
    }, [load]);

    const snap = data?.snapshot || {};
    const targets = data?.targets || {};
    const status = data?.status || {};
    const equip = data?.equipment || {};

    const trendData = (data?.trend || []).map((d) => ({
        ...d,
        label: new Date(d.date).toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
    }));

    return (
        <div className="fixed inset-0 z-[80] bg-slate-900/80 flex items-start sm:items-center justify-center overflow-y-auto" data-testid="equipment-dashboard-modal">
            <div className="relative w-full max-w-6xl my-4 mx-2 rounded-lg overflow-hidden" style={{ background: "linear-gradient(180deg, #050A1F 0%, #0B1430 100%)", boxShadow: "0 0 0 1px rgba(34,211,238,0.2), 0 20px 80px rgba(0,0,0,0.6)" }}>
                {/* Close */}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                    data-testid="equipment-dashboard-close"
                >
                    <X size={18} weight="bold" />
                </button>

                {/* Header */}
                <div className="px-4 sm:px-8 pt-6 pb-5 border-b border-cyan-400/10 flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-2xl sm:text-4xl text-white uppercase tracking-wide leading-tight" data-testid="equipment-name">{equip.name || "—"}</div>
                        <div className="text-cyan-300 font-display font-bold text-sm tracking-[0.18em] mt-1 uppercase">Equipment Performance Dashboard</div>
                        <div className="text-slate-400 text-xs mt-1">{equip.division} · {data?.is_legacy_auto ? "Auto-tracked (override available)" : "Manual entry"}</div>
                    </div>
                    <div className="flex flex-col gap-1.5 items-end">
                        <div className="px-3 py-1.5 rounded-md flex items-center gap-2 text-xs text-slate-200 flex-wrap" style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.2)" }}>
                            <Calendar size={13} className="text-cyan-300" />
                            <span className="text-slate-400">From</span>
                            <input
                                type="date"
                                value={startDate}
                                max={endDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="bg-transparent text-white tabular outline-none cursor-pointer"
                                data-testid="equipment-start-date-input"
                            />
                            <span className="text-slate-400">to</span>
                            <input
                                type="date"
                                value={endDate}
                                min={startDate}
                                max={todayStr()}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="bg-transparent text-white tabular outline-none cursor-pointer"
                                data-testid="equipment-end-date-input"
                            />
                        </div>
                        <div className="flex items-center gap-1 flex-wrap justify-end" data-testid="equipment-date-presets">
                            {PRESETS.map((p) => {
                                const active = startDate === p.start() && endDate === p.end();
                                return (
                                    <button
                                        key={p.key}
                                        onClick={() => { setStartDate(p.start()); setEndDate(p.end()); }}
                                        className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-sm transition-colors ${active ? "bg-cyan-400 text-slate-900 font-bold" : "bg-white/5 hover:bg-white/15 text-cyan-200"}`}
                                        data-testid={`preset-${p.key}`}
                                    >
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="px-3 py-1.5 rounded-md flex items-center gap-2 text-xs text-slate-200" style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.2)" }}>
                            <Clock size={13} className="text-cyan-300" />
                            <span className="text-slate-400">Time:</span>
                            <span className="text-white tabular">{now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
                        </div>
                    </div>
                </div>

                {/* Tab navigation */}
                <div className="px-4 sm:px-8 border-b border-cyan-400/15 bg-slate-950/40 flex items-center gap-1 overflow-x-auto" data-testid="equipment-tabs">
                    {[
                        { key: "performance", label: "Performance", Icon: ChartLineUp },
                        { key: "notes", label: "Notes", Icon: Notepad },
                        { key: "faults", label: "Fault Library", Icon: Lightning },
                        { key: "ai", label: "AI Fault Finder", Icon: MagicWand },
                    ].map(({ key, label, Icon }) => {
                        const active = tab === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setTab(key)}
                                className={`text-xs uppercase tracking-wide font-bold px-3 py-2.5 inline-flex items-center gap-1.5 border-b-2 transition-colors ${active ? "text-cyan-300 border-cyan-400" : "text-slate-400 border-transparent hover:text-slate-200"}`}
                                data-testid={`equipment-tab-${key}`}
                            >
                                <Icon size={13} weight="bold" /> {label}
                            </button>
                        );
                    })}
                </div>

                {/* Performance tab */}
                {tab === "performance" && (
                <>
                {/* Action bar / status badge — badge visible to all, action buttons only to admins/planners */}
                <div className="px-4 sm:px-8 py-3 bg-cyan-400/5 border-b border-cyan-400/10 flex items-center gap-3 flex-wrap">
                    {canInput && (
                        <>
                            <button
                                onClick={() => setShowLog(true)}
                                className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-bold uppercase tracking-wide text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5"
                                data-testid="log-equipment-metric-btn"
                            >
                                <Plus size={13} weight="bold" /> Log daily performance
                            </button>
                            <button
                                onClick={() => setShowTargets(true)}
                                className="bg-white/10 hover:bg-white/20 text-white text-xs uppercase tracking-wide px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5"
                                data-testid="edit-targets-btn"
                            >
                                <Target size={13} weight="bold" /> Edit targets
                            </button>
                        </>
                    )}
                    {snap.source && (
                        <span className={`ml-auto text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm ${snap.is_aggregate ? "bg-purple-500/20 text-purple-200 border border-purple-500/40" : snap.source === "manual" ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"}`} data-testid="snapshot-source-badge">
                            {snap.is_aggregate ? `Aggregate · ${snap.days_in_range} days · ${startDate} → ${endDate}` : (snap.source === "manual" ? "Manual entry" : "Auto-calculated")}
                        </span>
                    )}
                </div>

                {loading && !data ? (
                    <div className="p-16 text-center text-slate-400">Loading dashboard…</div>
                ) : !data ? null : (
                    <>
                        {/* Gauges */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 px-4 sm:px-8 py-6" data-testid="equipment-gauges">
                            <EquipmentGauge
                                value={snap.availability_pct}
                                target={targets.availability_pct}
                                unit="%"
                                label="AVAILABILITY"
                                color="#22D3EE"
                                aboveTarget={status.availability === "above"}
                            />
                            <EquipmentGauge
                                value={snap.utilisation_pct}
                                target={targets.utilisation_pct}
                                unit="%"
                                label="UTILISATION"
                                color="#F0C81F"
                                aboveTarget={status.utilisation === "above"}
                            />
                            <EquipmentGauge
                                value={snap.reliability_pct}
                                target={targets.reliability_pct}
                                unit="%"
                                label="RELIABILITY"
                                color="#00E676"
                                aboveTarget={status.reliability === "above"}
                            />
                            <EquipmentGauge
                                value={snap.mtbf_hrs}
                                max={96}
                                target={targets.mtbf_hrs}
                                unit="HOURS"
                                label="MTBF"
                                color="#A78BFA"
                                aboveTarget={status.mtbf === "above"}
                            />
                        </div>

                        {/* Bottom row: summary + trend */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-4 sm:px-8 pb-6">
                            {/* Performance Summary */}
                            <div className="rounded-lg p-4 sm:p-5" style={{ background: "linear-gradient(180deg, #0B1430 0%, #050A1F 100%)", border: "1px solid rgba(34,211,238,0.15)" }}>
                                <div className="text-cyan-300 font-display font-bold tracking-[0.18em] text-sm mb-3 uppercase flex items-center justify-between flex-wrap gap-2">
                                    <span>Performance Summary</span>
                                    {snap.is_aggregate && <span className="text-[10px] text-purple-200 font-normal normal-case tracking-normal">hours summed · MTTR averaged</span>}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="performance-summary">
                                    <SummaryTile Icon={Calendar} color="#22D3EE" label="Operating Hours" value={snap.operating_hrs} suffix="hrs" />
                                    <SummaryTile Icon={Wrench} color="#F0C81F" label="Planned Downtime" value={snap.planned_downtime_hrs} suffix="hrs" />
                                    <SummaryTile Icon={Warning} color="#EF4444" label="Unplanned Downtime" value={snap.unplanned_downtime_hrs} suffix="hrs" />
                                    <SummaryTile Icon={Clock} color="#00E676" label="MTTR" value={snap.mttr_hrs} suffix="hrs" />
                                </div>
                                {data.is_legacy_auto && snap.source === "auto" && (
                                    <div className="mt-3 text-[11px] text-cyan-200/70 leading-snug">
                                        These figures are auto-calculated from vessel-route activity for this conveyor. Log a manual entry below to override.
                                    </div>
                                )}
                                {snap.source === "manual" && (
                                    <div className="mt-3 text-[11px] text-amber-200/70 leading-snug">
                                        Manual entry by {data?.snapshot?.updated_by_name || "operator"} · {snap.notes ? `“${snap.notes}”` : "no notes"}
                                    </div>
                                )}
                            </div>
                            {/* Trend */}
                            <div className="rounded-lg p-4 sm:p-5" style={{ background: "linear-gradient(180deg, #0B1430 0%, #050A1F 100%)", border: "1px solid rgba(34,211,238,0.15)" }}>
                                <div className="text-cyan-300 font-display font-bold tracking-[0.18em] text-sm mb-3 uppercase">Trend <span className="text-slate-400 font-normal normal-case tracking-normal text-xs">({data.days_in_range} day{data.days_in_range === 1 ? "" : "s"})</span></div>
                                <div className="h-[200px]" data-testid="performance-trend-chart">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={trendData} margin={{ top: 5, right: 6, left: -10, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} stroke="rgba(255,255,255,0.2)" />
                                            <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#94a3b8" }} stroke="rgba(255,255,255,0.2)" domain={[0, 100]} />
                                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#94a3b8" }} stroke="rgba(255,255,255,0.2)" />
                                            <Tooltip contentStyle={{ background: "#0B1430", border: "1px solid rgba(34,211,238,0.3)", fontSize: 11 }} labelStyle={{ color: "#22D3EE" }} />
                                            <Legend wrapperStyle={{ fontSize: 10 }} />
                                            <Line yAxisId="left" type="monotone" dataKey="availability_pct" name="Availability" stroke="#22D3EE" dot={{ r: 3 }} strokeWidth={2} />
                                            <Line yAxisId="left" type="monotone" dataKey="utilisation_pct" name="Utilisation" stroke="#F0C81F" dot={{ r: 3 }} strokeWidth={2} />
                                            <Line yAxisId="left" type="monotone" dataKey="reliability_pct" name="Reliability" stroke="#00E676" dot={{ r: 3 }} strokeWidth={2} />
                                            <Line yAxisId="right" type="monotone" dataKey="mtbf_hrs" name="MTBF (hrs)" stroke="#A78BFA" dot={{ r: 3 }} strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        <div className="px-4 sm:px-8 pb-5 pt-1 text-center text-[11px] text-slate-400 inline-flex items-center justify-center w-full gap-2 border-t border-cyan-400/10">
                            <Target size={11} className="text-cyan-400" />
                            <span>Targets represent daily performance goals for {equip.name}. {data?.targets_are_default && canInput && <button onClick={() => setShowTargets(true)} className="underline text-cyan-300 ml-1">Set custom targets</button>}</span>
                        </div>
                    </>
                )}
                </>
                )}
                {tab === "notes" && (
                    <EquipmentNotesPanel equipmentId={equipmentId} user={user} canEdit={canEditNotes} />
                )}
                {tab === "faults" && (
                    <EquipmentFaultsPanel equipmentId={equipmentId} canEdit={canEditFaults} />
                )}
                {tab === "ai" && (
                    <EquipmentFaultFinderPanel equipmentId={equipmentId} equipmentName={equip?.name || ""} canRun={canRunFaultFinder} user={user} />
                )}
            </div>

            {showLog && (
                <LogEquipmentMetricsModal
                    equipmentId={equipmentId}
                    equipmentName={equip.name}
                    initialDate={endDate}
                    isLegacyAuto={!!data?.is_legacy_auto}
                    autoSnapshot={data?.is_legacy_auto && snap.source === "auto" ? snap : null}
                    onClose={() => setShowLog(false)}
                    onSaved={() => { setShowLog(false); load(); toast.success("Daily performance saved"); }}
                />
            )}
            {showTargets && (
                <EditEquipmentTargetsModal
                    equipmentId={equipmentId}
                    equipmentName={equip.name}
                    initial={targets}
                    onClose={() => setShowTargets(false)}
                    onSaved={() => { setShowTargets(false); load(); toast.success("Targets updated"); }}
                />
            )}
        </div>
    );
}

function SummaryTile({ Icon, color, label, value, suffix }) {
    const has = value != null && !isNaN(value);
    return (
        <div className="rounded-md p-3 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Icon size={22} weight="duotone" color={color} className="mx-auto mb-1" />
            <div className="text-[10px] uppercase tracking-wider text-slate-300 leading-tight">{label}</div>
            <div className="font-display font-black text-xl tabular mt-1" style={{ color }}>
                {has ? Number(value).toFixed(1) : "—"} <span className="text-[10px] font-normal text-slate-400">{suffix}</span>
            </div>
        </div>
    );
}
